import React, { useEffect, useMemo, useState } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../context/AuthContext';

const cardBase = 'rounded-2xl border border-gray-100 bg-white shadow-sm';
const labelBase = 'text-xs font-semibold uppercase tracking-wide text-gray-500';
const inputBase =
    'mt-1 block w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-gray-900 placeholder:text-gray-400 shadow-sm focus:border-[#18216D] focus:ring-4 focus:ring-[#18216D]/10 transition font-bold';

const sections = [
    { id: 'account', title: 'Account', description: 'Identity and authentication' },
    { id: 'academic', title: 'Academic', description: 'Grade and teaching details' },
    { id: 'security', title: 'Security', description: 'Password and authentication' },
];

const ProfilePage = () => {
    const { get, put, post } = useApi();
    const { loadUser } = useAuth();
    const [profile, setProfile] = useState(null);
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        grade: '',
    });
    const [baselineForm, setBaselineForm] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeSection, setActiveSection] = useState('account');
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [passwordStatus, setPasswordStatus] = useState({ type: '', message: '' });
    const [passwordSaving, setPasswordSaving] = useState(false);
    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirm: false,
    });
    const [assignedCourses, setAssignedCourses] = useState([]);

    // Password Validation Rules
    const passwordValidations = useMemo(() => ({
        length: passwordData.new_password.length >= 8,
        uppercase: /[A-Z]/.test(passwordData.new_password),
        lowercase: /[a-z]/.test(passwordData.new_password),
        number: /[0-9]/.test(passwordData.new_password),
        special: /[^A-Za-z0-9]/.test(passwordData.new_password),
    }), [passwordData.new_password]);

    const isPasswordValid = Object.values(passwordValidations).every(Boolean);

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                setLoading(true);
                const data = await get('/api/auth/profile/');
                setProfile(data);
                const normalized = {
                    first_name: data.first_name || '',
                    last_name: data.last_name || '',
                    email: data.email || '',
                    phone: data.phone || '',
                    grade: data.grade || '',
                };
                setFormData(normalized);
                setBaselineForm(normalized);
                
                // Fetch assigned courses if teacher
                if (data.role === 'teacher') {
                    get('/api/teacher/courses/')
                        .then(res => {
                            const courses = Array.isArray(res) ? res : (res?.courses || []);
                            setAssignedCourses(courses);
                        })
                        .catch(err => {
                            console.error('Courses fetch error:', err);
                            setAssignedCourses([]);
                        });
                }
            } catch (err) {
                setStatus({ type: 'error', message: 'Unable to load your profile right now.' });
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [get]);

    const handleChange = (event) => {
        const { name, value } = event.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const hasChanges = useMemo(() => {
        if (!baselineForm) return false;
        return JSON.stringify(baselineForm) !== JSON.stringify(formData);
    }, [baselineForm, formData]);

    const handleReset = () => {
        if (baselineForm) {
            setFormData(baselineForm);
            setStatus({ type: '', message: '' });
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!hasChanges) return;
        setSaving(true);
        setStatus({ type: '', message: '' });
        try {
            const updated = await put('/api/auth/profile/', formData);
            setProfile(updated);
            const normalized = {
                first_name: updated.first_name || '',
                last_name: updated.last_name || '',
                email: updated.email || '',
                phone: updated.phone || '',
                grade: updated.grade || '',
            };
            setFormData(normalized);
            setBaselineForm(normalized);
            setStatus({ type: 'success', message: 'Settings saved successfully.' });
            await loadUser();
        } catch (err) {
            setStatus({
                type: 'error',
                message: err.response?.data?.detail || 'Unable to save changes.',
            });
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = (event) => {
        const { name, value } = event.target;
        setPasswordData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePasswordSubmit = async (event) => {
        event.preventDefault();
        setPasswordStatus({ type: '', message: '' });

        if (passwordData.new_password !== passwordData.confirm_password) {
            setPasswordStatus({ type: 'error', message: 'New passwords do not match.' });
            return;
        }

        setPasswordSaving(true);
        try {
            await post('/api/auth/password-reset-manual/', passwordData);
            setPasswordStatus({ type: 'success', message: 'Password updated successfully.' });
            setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
        } catch (err) {
            setPasswordStatus({
                type: 'error',
                message: err.response?.data?.detail || 'Unable to update password.',
            });
        } finally {
            setPasswordSaving(false);
        }
    };

    const fullName = useMemo(
        () => (profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : ''),
        [profile]
    );

    const sectionNav = useMemo(() => {
        const visibleSections = profile?.role === 'parent'
            ? sections.filter(s => s.id !== 'academic')
            : sections;
        
        return visibleSections.map((section) => ({
            ...section,
            active: activeSection === section.id,
        }));
    }, [profile, activeSection]);

    if (loading) {
        return (
            <div className="max-w-5xl mx-auto p-6 animate-pulse space-y-4">
                <div className="h-32 rounded-2xl bg-gray-200" />
                <div className="h-64 rounded-2xl bg-gray-200" />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="max-w-3xl mx-auto p-6 text-center text-gray-600">
                <p>We couldn't load your settings. Please refresh the page.</p>
            </div>
        );
    }

    const stats = [
        {
            label: 'System ID',
            value: profile.role === 'teacher' ? (profile.teacher_profile?.teacher_id || '--') : (profile.student_id || '--'),
            icon: 'fas fa-id-card',
        },
    ];

    const teacherProfile = profile.teacher_profile;



    return (
        <div className="max-w-6xl mx-auto py-10 px-4 sm:px-6 lg:px-8 space-y-8">
            <header className={`${cardBase} p-6 flex flex-col gap-6`}>
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                    <div className="flex items-center gap-5">
                        <div className="h-20 w-20 rounded-2xl bg-[#18216D] text-white flex items-center justify-center text-3xl font-black shadow-lg shadow-indigo-900/20">
                            {(fullName || '??').slice(0, 1).toUpperCase()}
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FFC425] font-black leading-none mb-2">Portal Settings</p>
                            <h1 className="text-3xl font-black text-[#18216D] tracking-tight leading-none">{fullName || 'Unnamed user'}</h1>
                            <p className="text-sm text-slate-400 capitalize font-medium mt-2">{profile.role}</p>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 w-full lg:w-auto">
                        {stats.map((stat) => (
                            <div key={stat.label} className="rounded-2xl bg-slate-50 p-5 text-center border border-slate-100 min-w-[120px]">
                                <p className="text-[9px] uppercase text-slate-400 font-black tracking-widest">{stat.label}</p>
                                <p className="text-xl font-black text-[#18216D] mt-2 flex items-center justify-center gap-2">
                                    <i className={`${stat.icon} text-[#FFC425]`} />
                                    {stat.value}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {sectionNav.map((section) => (
                        <button
                            key={section.id}
                            onClick={() => setActiveSection(section.id)}
                            className={`px-5 py-2.5 rounded-xl text-[10px] uppercase font-black tracking-widest transition-all ${section.active ? 'bg-[#18216D] text-white shadow-lg shadow-indigo-900/20' : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                }`}
                        >
                            {section.title}
                        </button>
                    ))}
                </div>
            </header>

            <div className="grid gap-6 lg:grid-cols-[260px_1fr]">
                <aside className={`${cardBase} p-6 space-y-4`}>
                    <p className="text-xs uppercase text-gray-400 font-semibold">Sections</p>
                    <nav className="space-y-2">
                        {sectionNav.map((section) => (
                            <button
                                key={section.id}
                                onClick={() => setActiveSection(section.id)}
                                className={`w-full text-left px-4 py-4 rounded-xl transition-all border-2 ${section.active
                                    ? 'bg-[#18216D]/5 border-[#18216D] text-[#18216D]'
                                    : 'border-transparent text-slate-400 hover:text-slate-600'
                                    }`}
                            >
                                <p className="text-sm font-black uppercase tracking-tight">{section.title}</p>
                                <p className="text-xs text-gray-500">{section.description}</p>
                            </button>
                        ))}
                    </nav>
                    <div className="pt-4 border-t border-gray-100 text-sm text-gray-500 space-y-2">
                        <p>Need help updating your information?</p>
                        <a href="mailto:support@kiandaschool.com" className="text-[#18216D] font-black underline decoration-[#FFC425] decoration-2 underline-offset-4">
                            Contact support
                        </a>
                    </div>
                </aside>

                <form onSubmit={handleSubmit} className={`${cardBase} p-6 space-y-6`}>
                    {status.message && (
                        <div
                            className={`rounded-xl px-4 py-3 text-sm ${status.type === 'success'
                                ? 'bg-green-50 text-green-700 border border-green-200'
                                : 'bg-red-50 text-red-700 border border-red-200'
                                }`}
                        >
                            {status.message}
                        </div>
                    )}

                    {activeSection === 'account' && (
                        <div className="space-y-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Account</h2>
                                <p className="text-sm text-gray-500">Update your identity and authentication details.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelBase} htmlFor="first_name">
                                        First name
                                    </label>
                                    <input
                                        id="first_name"
                                        name="first_name"
                                        type="text"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        className={inputBase}
                                    />
                                </div>
                                <div>
                                    <label className={labelBase} htmlFor="last_name">
                                        Last name
                                    </label>
                                    <input
                                        id="last_name"
                                        name="last_name"
                                        type="text"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        className={inputBase}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBase} htmlFor="email">
                                        Email
                                    </label>
                                    <input
                                        id="email"
                                        name="email"
                                        type="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={inputBase}
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className={labelBase} htmlFor="phone">
                                        Phone
                                    </label>
                                    <input
                                        id="phone"
                                        name="phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        className={inputBase}
                                        placeholder="+254..."
                                    />
                                </div>
                            </div>
                        </div>
                    )}



                    {activeSection === 'academic' && (
                        <div className="space-y-4">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Academic</h2>
                                <p className="text-sm text-gray-500">Grade levels and teaching assignments.</p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {profile.role === 'student' && (
                                    <div>
                                        <label className={labelBase} htmlFor="grade">
                                            Grade / Level
                                        </label>
                                        <select
                                            id="grade"
                                            name="grade"
                                            value={formData.grade}
                                            onChange={handleChange}
                                            className={inputBase}
                                        >
                                            <option value="">Select Grade</option>
                                            {[...Array(10)].map((_, i) => (
                                                <option key={i + 1} value={`Grade ${i + 1}`}>Grade {i + 1}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    <p className={labelBase}>System Identifier</p>
                                    <div className="rounded-xl border border-gray-200 px-4 py-3 text-sm text-gray-900 bg-slate-50 font-black">
                                        {profile.role === 'teacher' ? (profile.teacher_profile?.teacher_id || '--') : (profile.student_id || '--')}
                                    </div>
                                </div>
                            </div>

                            {profile.role === 'teacher' && (
                                <div className="mt-8 space-y-4">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-black uppercase tracking-widest text-[#18216D]">Academic Performance Metrics</h3>
                                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-tight">Term-over-Term Comparison</span>
                                    </div>
                                    <div className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50/50">
                                        <table className="w-full text-left text-sm">
                                            <thead>
                                                <tr className="border-b border-slate-100">
                                                    <th className="px-4 py-3 text-[10px] uppercase font-black text-slate-400">Learning Area</th>
                                                    <th className="px-4 py-3 text-[10px] uppercase font-black text-slate-400 text-center">Mean Score</th>
                                                    <th className="px-4 py-3 text-[10px] uppercase font-black text-slate-400 text-right">Trend</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(assignedCourses) && assignedCourses.length > 0 ? (
                                                    assignedCourses.map((course) => (
                                                        <tr key={course.id} className="border-b border-slate-100 last:border-0 hover:bg-white transition-colors">
                                                            <td className="px-4 py-4">
                                                                <p className="font-black text-[#18216D] leading-tight">{course.name}</p>
                                                                <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">{course.grade_level_name} • {course.stream_display_name}</p>
                                                            </td>
                                                            <td className="px-4 py-4 text-center font-black text-[#18216D]">
                                                                {course.current_mean_score ? Number(course.current_mean_score).toFixed(2) : '--'}
                                                            </td>
                                                            <td className="px-4 py-4 text-right">
                                                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${
                                                                    course.performance_metric === 'Improving' ? 'bg-indigo-50 text-indigo-600' :
                                                                    course.performance_metric === 'Declining' ? 'bg-rose-50 text-rose-600' :
                                                                    'bg-slate-100 text-slate-500'
                                                                }`}>
                                                                    <i className={`fas ${
                                                                        course.performance_metric === 'Improving' ? 'fa-arrow-trend-up' :
                                                                        course.performance_metric === 'Declining' ? 'fa-arrow-trend-down' :
                                                                        'fa-minus'
                                                                    }`} />
                                                                    {course.performance_metric}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="3" className="px-4 py-8 text-center text-slate-400 font-bold uppercase text-[10px] tracking-widest">
                                                            No active learning areas assigned to your profile
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {activeSection === 'security' && (
                        <div className="space-y-6">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900">Security</h2>
                                <p className="text-sm text-gray-500">Update your password to keep your account secure.</p>
                            </div>
                            
                            {passwordStatus.message && (
                                <div
                                    className={`rounded-xl px-4 py-3 text-sm ${passwordStatus.type === 'success'
                                        ? 'bg-green-50 text-green-700 border border-green-200'
                                        : 'bg-red-50 text-red-700 border border-red-200'
                                        }`}
                                >
                                    {passwordStatus.message}
                                </div>
                            )}

                            <div className="grid grid-cols-1 gap-4 max-w-md">
                                <div className="relative">
                                    <label className={labelBase} htmlFor="current_password">
                                        Current Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="current_password"
                                            name="current_password"
                                            type={showPasswords.current ? 'text' : 'password'}
                                            value={passwordData.current_password}
                                            onChange={handlePasswordChange}
                                            className={inputBase}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#18216D]"
                                        >
                                            <i className={`fas ${showPasswords.current ? 'fa-eye-slash' : 'fa-eye'}`} />
                                        </button>
                                    </div>
                                </div>
                                <div className="pt-4 border-t border-gray-50 relative">
                                    <label className={labelBase} htmlFor="new_password">
                                        New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="new_password"
                                            name="new_password"
                                            type={showPasswords.new ? 'text' : 'password'}
                                            value={passwordData.new_password}
                                            onChange={handlePasswordChange}
                                            className={inputBase}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#18216D]"
                                        >
                                            <i className={`fas ${showPasswords.new ? 'fa-eye-slash' : 'fa-eye'}`} />
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Password Requirements Feedback */}
                                {passwordData.new_password && (
                                    <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-1.5">
                                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                                            Password Requirements
                                        </p>
                                        <div className="grid grid-cols-1 gap-1.5 text-xs font-semibold">
                                            <div className={`flex items-center gap-2 ${passwordValidations.length ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <i className={`fas ${passwordValidations.length ? 'fa-check-circle' : 'fa-circle'} w-4`} />
                                                At least 8 characters
                                            </div>
                                            <div className={`flex items-center gap-2 ${passwordValidations.uppercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <i className={`fas ${passwordValidations.uppercase ? 'fa-check-circle' : 'fa-circle'} w-4`} />
                                                Uppercase letter
                                            </div>
                                            <div className={`flex items-center gap-2 ${passwordValidations.lowercase ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <i className={`fas ${passwordValidations.lowercase ? 'fa-check-circle' : 'fa-circle'} w-4`} />
                                                Lowercase letter
                                            </div>
                                            <div className={`flex items-center gap-2 ${passwordValidations.number ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <i className={`fas ${passwordValidations.number ? 'fa-check-circle' : 'fa-circle'} w-4`} />
                                                Number
                                            </div>
                                            <div className={`flex items-center gap-2 ${passwordValidations.special ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                <i className={`fas ${passwordValidations.special ? 'fa-check-circle' : 'fa-circle'} w-4`} />
                                                Special character
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <div className="relative">
                                    <label className={labelBase} htmlFor="confirm_password">
                                        Confirm New Password
                                    </label>
                                    <div className="relative">
                                        <input
                                            id="confirm_password"
                                            name="confirm_password"
                                            type={showPasswords.confirm ? 'text' : 'password'}
                                            value={passwordData.confirm_password}
                                            onChange={handlePasswordChange}
                                            className={inputBase}
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}
                                            className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#18216D]"
                                        >
                                            <i className={`fas ${showPasswords.confirm ? 'fa-eye-slash' : 'fa-eye'}`} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex justify-start pt-2">
                                <button
                                    type="button"
                                    onClick={handlePasswordSubmit}
                                    disabled={passwordSaving || !passwordData.current_password || !passwordData.new_password || !isPasswordValid}
                                    className={`rounded-xl px-8 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-white shadow-xl transition-all ${!passwordSaving && passwordData.current_password && passwordData.new_password && isPasswordValid
                                        ? 'bg-[#18216D] shadow-indigo-100 hover:bg-[#0D164F] hover:scale-[1.02]'
                                        : 'bg-slate-200 cursor-not-allowed'
                                        }`}
                                >
                                    {passwordSaving ? (
                                        <span className="flex items-center gap-2">
                                            <i className="fas fa-circle-notch fa-spin" />
                                            Updating...
                                        </span>
                                    ) : 'Update Password'}
                                </button>
                            </div>
                        </div>
                    )}

                    {activeSection !== 'security' && (
                        <div className="flex justify-end gap-3 pt-2">
                            <button
                                type="button"
                                onClick={handleReset}
                                disabled={!hasChanges || saving}
                                className={`rounded-xl border px-4 py-2 text-sm font-semibold ${hasChanges && !saving
                                    ? 'border-gray-200 text-gray-700 hover:bg-gray-50'
                                    : 'border-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                            >
                                Reset
                            </button>
                            <button
                                type="submit"
                                disabled={!hasChanges || saving}
                                className={`rounded-xl px-8 py-3 text-[10px] font-black uppercase tracking-[0.2em] text-white shadow-xl transition-all ${hasChanges && !saving
                                    ? 'bg-[#18216D] shadow-indigo-100 hover:bg-[#0D164F] hover:scale-[1.02] active:scale-[0.98]'
                                    : 'bg-slate-200 cursor-not-allowed'
                                    }`}
                            >
                                {saving ? (
                                    <span className="flex items-center gap-2">
                                        <i className="fas fa-circle-notch fa-spin" />
                                        Synchronizing…
                                    </span>
                                ) : 'Save Changes'}
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default ProfilePage;

